import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Select, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Buildings, Warning } from 'phosphor-react';
import { toast } from 'sonner';
import {
  centralApi,
  IEmpresaCarteira,
  identificacaoDaEmpresa,
  podeBuscarEmpresa,
} from '../../hooks/useCentralPermissoes';
import { useGruposDeEmpresas } from './GruposDeEmpresas';
import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Etiqueta,
  Eyebrow,
  MONO,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

const SEM_GRUPO = '__sem_grupo__';

function LinhaDeAlocacao({ empresa, grupos }: { empresa: IEmpresaCarteira; grupos: string[] }) {
  const queryClient = useQueryClient();
  const [atual, setAtual] = useState(empresa.grupo_de_empresas ?? null);
  const [destino, setDestino] = useState(empresa.grupo_de_empresas ?? SEM_GRUPO);
  const alvo = destino === SEM_GRUPO ? null : destino;

  const mover = useMutation({
    mutationFn: () => centralApi.alocarEmpresa(empresa.id, alvo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(
        alvo
          ? `${empresa.nome} agora está em ${alvo}`
          : `${empresa.nome} ficou sem grupo de empresas`,
      );
      setAtual(alvo);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? 'Não foi possível mover a empresa'),
  });

  return (
    <Flex
      align="center"
      gap="12px"
      p="10px 20px"
      borderTop="1px solid rgba(15,23,42,.05)"
      wrap="wrap"
    >
      <Buildings size={16} color={TEXTO_SUAVE} />
      <Box flex="1 1 240px" minW={0}>
        <Text fontSize="13.5px" fontWeight={600} noOfLines={1}>
          {empresa.nome}
        </Text>
        <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
          {identificacaoDaEmpresa(empresa)}
        </Text>
      </Box>
      <Etiqueta tom={atual ? 'info' : 'neutro'}>{atual ?? 'sem grupo'}</Etiqueta>
      <ArrowRight size={14} color={TEXTO_SUAVE} />
      <Select
        size="sm"
        w="220px"
        borderRadius="10px"
        bg="white"
        value={destino}
        aria-label={`Grupo de destino de ${empresa.nome}`}
        onChange={e => setDestino(e.target.value)}
      >
        {grupos.map(g => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
        <option value={SEM_GRUPO}>Sem grupo de empresas</option>
      </Select>
      <Button
        size="sm"
        borderRadius="999px"
        color="white"
        bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
        _hover={{ bg: AZUL_ESCURO }}
        isDisabled={alvo === atual}
        isLoading={mover.isPending}
        onClick={() => mover.mutate()}
      >
        Mover
      </Button>
    </Flex>
  );
}

/** Acha uma empresa e põe ela num grupo de empresas, sem abrir grupo nenhum. */
export function AlocacaoRapida() {
  const { lista, semRota } = useGruposDeEmpresas();
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 300);
    return () => clearTimeout(t);
  }, [digitado]);

  const { data: achadas = [], isFetching } = useQuery({
    queryKey: ['central', 'busca-empresas', busca],
    queryFn: () => centralApi.buscarEmpresas(busca),
    enabled: podeBuscarEmpresa(busca),
  });
  const nomes = lista.map(g => g.nome);

  return (
    <Flex direction="column" gap="16px">
      <Cartao p={0} overflow="hidden">
        <Box p="20px 22px 12px">
          <Eyebrow>ALOCAÇÃO RÁPIDA</Eyebrow>
          <Text
            fontSize="22px"
            lineHeight="28px"
            fontWeight={700}
            letterSpacing="-.3px"
            color={TEXTO}
          >
            Pôr uma empresa num grupo
          </Text>
          <Text fontSize="13px" color="#475569">
            Busque pelo nome, CNPJ ou ID, escolha o grupo de empresas de destino e clique em Mover.
            Grava na hora, uma empresa por vez.
          </Text>
        </Box>
        <Box px="22px" pb="14px" maxW="480px">
          <Busca
            valor={digitado}
            onChange={setDigitado}
            placeholder="Buscar empresa por nome, CNPJ ou ID"
          />
        </Box>
        {semRota && (
          <Flex
            mx="22px"
            mb="12px"
            gap="8px"
            align="flex-start"
            fontSize="12.5px"
            color="#92400E"
            bg="#FEFCE8"
            p="10px 12px"
            borderRadius="14px"
            boxShadow="inset 0 0 0 1px #FDE68A"
          >
            <Warning size={16} />
            <Text>
              Pré-visualização: a busca já funciona, mas mover depende do backend de grupos de
              empresas, que ainda não está nesta versão.
            </Text>
          </Flex>
        )}
        {podeBuscarEmpresa(busca) && isFetching && (
          <Flex justify="center" py={6}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {podeBuscarEmpresa(busca) &&
          !isFetching &&
          achadas.map(e => <LinhaDeAlocacao key={e.id} empresa={e} grupos={nomes} />)}
        {podeBuscarEmpresa(busca) && !isFetching && achadas.length === 0 && (
          <Text px="22px" pb="16px" fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma empresa com esse nome, CNPJ ou ID.
          </Text>
        )}
        {busca.length < 2 && (
          <Text px="22px" pb="18px" fontSize="13px" color={TEXTO_SUAVE}>
            Digite o nome (2 letras ou mais), o ID ou o CNPJ.
          </Text>
        )}
      </Cartao>
    </Flex>
  );
}
